import { throttle } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutationObserverForEditor } from './useMutationObserverForEditor';
import { useResizeObserver } from './useResizeObserver';
import type { AnimationType } from '../TextParticles.types';

export type ComputedTextStyle = {
  color: string;
  font: string;
  letterSpacing: string;
  textTransform: string;
  boundingBox: DOMRect;
  animationDirection: number;
  spread: number;
  duration: number;
  animationType: AnimationType;
  particleStartSize: number;
  particleEndSize: number;
  particleStartOpacity: number;
  particleEndOpacity: number;
  windDirection: number;
  windSpeed: number;
  turbulence: number;
  particleColor1: string;
  particleColor2: string;
  particleColor3: string;
  particleColor4: string;
  particleColorCount: number;
  spreadAreaVisible: boolean;
};

export const useComputedStyle = (
  compId: string,
  elementRef: React.RefObject<HTMLElement | null>,
  isEditor: boolean,
): ComputedTextStyle | undefined => {
  const [computedStyle, setComputedStyle] = useState<
    ComputedTextStyle | undefined
  >(undefined);

  const previousComputedStyleCacheRef = useRef<string>('');

  const getComputedStyleCache = useCallback(
    (_computedStyle: ComputedTextStyle | undefined) => {
      if (!_computedStyle) {
        return '';
      }

      const boundingBoxData = {
        width: Math.round(_computedStyle.boundingBox.width),
        height: Math.round(_computedStyle.boundingBox.height),
      };

      const cacheData = {
        ..._computedStyle,
        boundingBox: boundingBoxData,
      };

      return JSON.stringify(cacheData);
    },
    [],
  );

  const applyComputedStyle = useCallback(() => {
    // assumes styles changed & fonts loaded
    const boundingBox = elementRef.current?.getBoundingClientRect();

    if (
      !elementRef.current ||
      !boundingBox ||
      (boundingBox.width === 0 && boundingBox.height === 0)
    ) {
      setComputedStyle(undefined);
    } else {
      const nextComputedStyle = window.getComputedStyle(elementRef.current);

      const nextDirection = Number(
        nextComputedStyle.getPropertyValue('--animation-direction'),
      );
      const nextSpread = Number(nextComputedStyle.getPropertyValue('--spread'));
      const nextDuration = Number(
        nextComputedStyle.getPropertyValue('--duration'),
      );
      const nextAnimationType =
        nextComputedStyle.getPropertyValue('--animation-type');
      const nextParticleStartSize = Number(
        nextComputedStyle.getPropertyValue('--particle-start-size'),
      );
      const nextParticleEndSize = Number(
        nextComputedStyle.getPropertyValue('--particle-end-size'),
      );
      const nextParticleStartOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-start-opacity'),
      );
      const nextParticleEndOpacity = Number(
        nextComputedStyle.getPropertyValue('--particle-end-opacity'),
      );
      const nextWindDirection = Number(
        nextComputedStyle.getPropertyValue('--wind-direction'),
      );
      const nextWindSpeed = Number(
        nextComputedStyle.getPropertyValue('--wind-speed'),
      );
      const nextTurbulence = Number(
        nextComputedStyle.getPropertyValue('--turbulence'),
      );
      const particleColor1 = String(
        nextComputedStyle.getPropertyValue('--text-particles-color-1'),
      );
      const particleColor2 = String(
        nextComputedStyle.getPropertyValue('--text-particles-color-2'),
      );
      const particleColor3 = String(
        nextComputedStyle.getPropertyValue('--text-particles-color-3'),
      );
      const particleColor4 = String(
        nextComputedStyle.getPropertyValue('--text-particles-color-4'),
      );
      const particleColorCount = Number(
        nextComputedStyle.getPropertyValue('--particle-color-count'),
      );
      const nextTextTransform =
        nextComputedStyle.getPropertyValue('text-transform') ||
        nextComputedStyle.getPropertyValue('--text-transform');
      const nextTextColor =
        nextComputedStyle.getPropertyValue('color') ||
        nextComputedStyle.getPropertyValue('--color');
      const nextSpreadAreaVisible = nextComputedStyle.getPropertyValue(
        '--spread-area-visible',
      );
      const nextTextComputedStyle: ComputedTextStyle = {
        font: nextComputedStyle.font,
        letterSpacing: nextComputedStyle.letterSpacing,
        textTransform: nextTextTransform,
        boundingBox,
        animationDirection: nextDirection,
        spread: nextSpread,
        duration: nextDuration,
        animationType: nextAnimationType as AnimationType,
        particleStartSize: nextParticleStartSize,
        particleEndSize: nextParticleEndSize,
        particleStartOpacity: nextParticleStartOpacity,
        particleEndOpacity: nextParticleEndOpacity,
        windDirection: nextWindDirection,
        windSpeed: nextWindSpeed,
        turbulence: nextTurbulence,
        particleColor1,
        particleColor2,
        particleColor3,
        particleColor4,
        particleColorCount,
        color: nextTextColor,
        spreadAreaVisible: nextSpreadAreaVisible === '1',
      };

      const previousComputedStyleCache = previousComputedStyleCacheRef.current;
      const nextComputedStyleCache = getComputedStyleCache(
        nextTextComputedStyle,
      );

      if (previousComputedStyleCache !== nextComputedStyleCache) {
        previousComputedStyleCacheRef.current = nextComputedStyleCache;
        setComputedStyle(nextTextComputedStyle);
      }
    }
  }, [elementRef, getComputedStyleCache]);

  const applyComputedStyleAfterFontsReady = useCallback(() => {
    document.fonts.ready.then(applyComputedStyle, () => {});
  }, [applyComputedStyle]);

  const applyComputedStyleAfterFontsReadyThrottled = useMemo(() => {
    return throttle(applyComputedStyleAfterFontsReady, 100, {
      leading: true,
      trailing: true,
    });
  }, [applyComputedStyleAfterFontsReady]);

  useMutationObserverForEditor({
    isEditor,
    compId,
    onStyleChange: applyComputedStyleAfterFontsReady,
  });

  useResizeObserver({
    elementRef,
    onResize: applyComputedStyleAfterFontsReadyThrottled,
  });

  useEffect(() => {
    applyComputedStyleAfterFontsReady();
  }, [applyComputedStyleAfterFontsReady]);

  return computedStyle;
};
export type UseComputedStyleResult = ReturnType<typeof useComputedStyle>;
